import type { FC, ReactNode } from 'react'
import type { AttendanceRecord, AttendanceStatus, Student } from '@/types'

import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { DEMO_TODAY, formatDate } from '@/lib/dates'
import { Seo } from '@/components/generals/seo'
import { Button, ButtonLink } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/feedback'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { HistoryPicker } from '@/components/app/history-picker'
import { PageHeader } from '@/components/app/page-header'
import { PageSkeleton } from '@/components/app/page-skeleton'
import { SearchField } from '@/components/app/search-field'
import { useAcademic } from '@/components/providers/academic-provider'
import { useConfirm } from '@/components/providers/confirm-provider'
import { useToast } from '@/components/providers/toast-provider'
import { useQueryParams } from '@/hooks/use-query-params'
import { ATTENDANCE_LABELS, ATTENDANCE_STATUSES } from '@/constants/academic'

import {
    THead,
    TBody,
    TD,
    TH,
    TR,
    Table
} from '@/components/ui/table'

import {
    ArrowRight,
    Check,
    ListChecks,
    Trash2,
    UserPlus,
    Zap
} from 'lucide-react'

const SOLID: Record<AttendanceStatus, string> = {
    H: 'border-success bg-success text-success-foreground shadow-sm shadow-success/25',
    S: 'border-warning bg-warning text-warning-foreground shadow-sm shadow-warning/25',
    I: 'border-info bg-info text-info-foreground shadow-sm shadow-info/25',
    A: 'border-destructive bg-destructive text-destructive-foreground shadow-sm shadow-destructive/25'
}

const HOVER: Record<AttendanceStatus, string> = {
    H: 'hover:border-success/60 hover:text-success',
    S: 'hover:border-warning/60 hover:text-warning',
    I: 'hover:border-info/60 hover:text-info',
    A: 'hover:border-destructive/60 hover:text-destructive'
}

const SUMMARY: Record<AttendanceStatus, { tile: string, dot: string }> = {
    H: { tile: 'border-success/25 bg-success/8', dot: 'bg-success' },
    S: { tile: 'border-warning/25 bg-warning/8', dot: 'bg-warning' },
    I: { tile: 'border-info/25 bg-info/8', dot: 'bg-info' },
    A: { tile: 'border-destructive/25 bg-destructive/8', dot: 'bg-destructive' }
}

type AttendanceSheetProps = {
    classId: string
    date: string
    students: Student[]
    saved: Map<string, AttendanceRecord>
    onReload: () => void
}

const AttendanceSheet: FC<AttendanceSheetProps> = ({ classId, date, students, saved, onReload }): ReactNode => {
    const { actions } = useAcademic()
    const confirm = useConfirm()
    const toast = useToast()

    const [draft, setDraft] = useState<Record<string, AttendanceStatus>>(() => {
        return Object.fromEntries(students.map(student => [student.id, saved.get(student.id)?.status ?? 'H']))
    })

    const [query, setQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState<AttendanceStatus | ''>('')
    const [bulkStatus, setBulkStatus] = useState<AttendanceStatus>('H')
    const [saving, setSaving] = useState(false)

    const hasSaved = saved.size > 0

    const counts = useMemo(() => {
        const result: Record<AttendanceStatus, number> = { H: 0, S: 0, I: 0, A: 0 }

        Object.values(draft).forEach(status => {
            result[status] += 1
        })

        return result
    }, [draft])

    const search = query.trim().toLowerCase()
    const visible = students.filter(student => {
        const matchesText = `${student.name} ${student.nis}`.toLowerCase().includes(search)
        const matchesStatus = !statusFilter || draft[student.id] === statusFilter

        return matchesText && matchesStatus
    })

    const handleSave = (): void => {
        setSaving(true)

        const result = actions.saveAttendance({ classId, date, statuses: draft })

        setSaving(false)

        if (result.ok) {
            toast.success(result.message ?? 'Absensi berhasil disimpan.')
            onReload()
        }
        else {
            toast.error('Gagal menyimpan', result.error)
        }
    }

    const handleDeleteRow = async (student: Student): Promise<void> => {
        const approved = await confirm({
            title: 'Hapus catatan absensi siswa ini?',
            description: `Status ${student.name} pada ${formatDate(date, 'short')} akan dihapus.`,
            confirmLabel: 'Ya, hapus'
        })

        if (!approved) return

        const result = actions.deleteStudentAttendance({ studentId: student.id, date })

        if (result.ok) {
            toast.success(result.message ?? 'Catatan absensi siswa dihapus.')
            onReload()
        }
    }

    return (
        <>
            <div className='mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4'>
                {ATTENDANCE_STATUSES.map(status => (
                    <div key={status} className={cn('flex items-center gap-3 rounded-xl border p-4', SUMMARY[status].tile)}>
                        <span className={cn('size-2.5 shrink-0 rounded-full', SUMMARY[status].dot)} aria-hidden='true' />

                        <span className='flex-1 text-sm font-medium text-muted-foreground'>
                            {ATTENDANCE_LABELS[status]}
                        </span>

                        <strong className='text-2xl leading-none font-bold text-foreground'>
                            {counts[status]}
                        </strong>
                    </div>
                ))}
            </div>

            <Card>
                <div className='flex flex-col gap-3 border-b px-5 py-4 lg:flex-row lg:items-center lg:justify-between'>
                    <div>
                        <h2 className='text-[15px] font-semibold text-foreground'>
                            Daftar kehadiran
                        </h2>

                        <p className='text-[13px] text-muted-foreground'>
                            {visible.length} dari {students.length} siswa
                        </p>
                    </div>

                    <div className='flex flex-col gap-2 sm:flex-row'>
                        <SearchField
                            label='Cari siswa'
                            placeholder='Cari nama atau NIS...'
                            value={query}
                            onChange={event => setQuery(event.target.value)}
                            wrapperClassName='sm:w-64'
                        />

                        <Select
                            aria-label='Filter status siswa'
                            value={statusFilter}
                            onChange={event => setStatusFilter(event.target.value as AttendanceStatus | '')}
                            className='h-9'
                            wrapperClassName='sm:w-44'
                        >
                            <option value=''>Semua status</option>
                            {ATTENDANCE_STATUSES.map(status => <option key={status} value={status}>{ATTENDANCE_LABELS[status]}</option>)}
                        </Select>
                    </div>
                </div>

                <div className='flex flex-col gap-3 border-b bg-muted/35 px-5 py-3.5 sm:flex-row sm:items-center sm:justify-between'>
                    <div className='flex items-start gap-3'>
                        <ListChecks className='mt-0.5 size-[18px] shrink-0 text-primary' aria-hidden='true' />

                        <div>
                            <p className='text-[13px] font-semibold text-foreground'>
                                Atur status sekaligus
                            </p>

                            <p className='text-xs text-muted-foreground'>
                                Pilih status untuk seluruh siswa di kelas ini.
                            </p>
                        </div>
                    </div>

                    <div className='flex gap-2'>
                        <Select
                            aria-label='Status untuk semua siswa'
                            value={bulkStatus}
                            onChange={event => setBulkStatus(event.target.value as AttendanceStatus)}
                            className='h-9'
                            wrapperClassName='w-36'
                        >
                            {ATTENDANCE_STATUSES.map(status => <option key={status} value={status}>{ATTENDANCE_LABELS[status]}</option>)}
                        </Select>

                        <Button
                            variant='outline'
                            size='sm'
                            className='h-9'
                            onClick={() => setDraft(Object.fromEntries(students.map(student => [student.id, bulkStatus])))}
                        >
                            Terapkan ke semua
                        </Button>
                    </div>
                </div>

                <Table>
                    <THead>
                        <tr>
                            <TH className='w-24'>No. Absen</TH>
                            <TH>Siswa</TH>
                            {ATTENDANCE_STATUSES.map(status => <TH key={status} className='w-20 text-center'>{ATTENDANCE_LABELS[status]}</TH>)}
                            {hasSaved && <TH className='w-24 text-center'>Hapus</TH>}
                        </tr>
                    </THead>

                    <TBody>
                        {visible.map(student => (
                            <TR key={student.id}>
                                <TD className='font-semibold text-muted-foreground tabular-nums'>
                                    {students.indexOf(student) + 1}
                                </TD>

                                <TD>
                                    <p className='font-semibold text-foreground'>{student.name}</p>
                                    <p className='text-xs text-muted-foreground'>NIS {student.nis}</p>
                                </TD>

                                {ATTENDANCE_STATUSES.map(status => {
                                    const checked = draft[student.id] === status

                                    return (
                                        <TD key={status} className='text-center'>
                                            <label className='inline-grid' title={ATTENDANCE_LABELS[status]}>
                                                <input
                                                    type='radio'
                                                    name={`status-${student.id}`}
                                                    value={status}
                                                    checked={checked}
                                                    onChange={() => setDraft(current => ({ ...current, [student.id]: status }))}
                                                    className='peer sr-only'
                                                    aria-label={`${ATTENDANCE_LABELS[status]}: ${student.name}`}
                                                />

                                                <span
                                                    className={cn(
                                                        'grid size-9 place-items-center rounded-lg border text-[13px] font-bold transition-[background-color,border-color,color,box-shadow] peer-focus-visible:ring-3 peer-focus-visible:ring-ring/40',
                                                        checked ? SOLID[status] : cn('border-input bg-card text-muted-foreground dark:bg-input/20', HOVER[status])
                                                    )}
                                                    aria-hidden='true'
                                                >
                                                    {status}
                                                </span>
                                            </label>
                                        </TD>
                                    )
                                })}

                                {hasSaved && (
                                    <TD className='text-center'>
                                        {saved.has(student.id) ? (
                                            <Button variant='danger-outline' size='xs' onClick={() => void handleDeleteRow(student)}>
                                                Hapus
                                            </Button>
                                        ) : (
                                            <span className='text-muted-foreground'>—</span>
                                        )}
                                    </TD>
                                )}
                            </TR>
                        ))}

                        {visible.length === 0 && (
                            <tr>
                                <td colSpan={hasSaved ? 7 : 6} className='px-5 py-12 text-center text-sm text-muted-foreground'>
                                    Tidak ada siswa yang cocok dengan pencarian.
                                </td>
                            </tr>
                        )}
                    </TBody>
                </Table>

                <div className='flex flex-col gap-3 border-t px-5 py-4 sm:flex-row sm:items-center sm:justify-between'>
                    <p className='text-[13px] text-muted-foreground'>
                        <strong className='font-semibold text-foreground'>{Object.keys(draft).length}</strong> dari {students.length} siswa sudah memiliki status
                    </p>

                    <Button onClick={handleSave} loading={saving} className='sm:px-6'>
                        <Check aria-hidden='true' />
                        Simpan absensi
                    </Button>
                </div>
            </Card>
        </>
    )
}

const AttendancePage: FC = (): ReactNode => {
    const { classes, students, attendance, actions } = useAcademic()
    const { ready, get, set } = useQueryParams()
    const confirm = useConfirm()
    const toast = useToast()

    const [version, setVersion] = useState(0)
    const [bulkStatus, setBulkStatus] = useState<AttendanceStatus>('H')
    const [bulkMode, setBulkMode] = useState<'all' | 'empty'>('all')

    if (!ready) return <PageSkeleton />

    const requestedClass = get('kelas')
    const classId = classes.some(item => item.id === requestedClass) ? requestedClass as string : classes[0]?.id ?? ''
    const requestedDate = get('tanggal') ?? ''
    const date = /^\d{4}-\d{2}-\d{2}$/.test(requestedDate) ? requestedDate : DEMO_TODAY

    const classStudents = students.filter(student => student.classId === classId)
    const saved = new Map(attendance.filter(record => record.date === date && classStudents.some(student => student.id === record.studentId)).map(record => [record.studentId, record]))
    const historyDates = Array.from(new Set(attendance.filter(record => record.classId === classId).map(record => record.date))).sort().reverse().slice(0, 30)

    const reload = (): void => setVersion(value => value + 1)

    const handleBulk = async (): Promise<void> => {
        const approved = await confirm({
            title: 'Terapkan status massal ke siswa di kelas ini?',
            description: `Status ${ATTENDANCE_LABELS[bulkStatus]} akan disimpan untuk ${bulkMode === 'all' ? 'semua siswa' : 'siswa yang belum diabsen'} pada ${formatDate(date, 'short')}.`,
            confirmLabel: 'Terapkan',
            tone: 'default'
        })

        if (!approved) return

        const result = actions.bulkAttendance({ classId, date, status: bulkStatus, mode: bulkMode })

        if (result.ok) {
            toast.success(result.message ?? 'Absensi massal diterapkan.')
            reload()
        }
    }

    const handleDeleteDate = async (): Promise<void> => {
        const approved = await confirm({
            title: `Hapus SEMUA catatan absensi kelas ini pada ${formatDate(date, 'short')}?`,
            description: 'Tindakan ini tidak dapat dibatalkan.',
            confirmLabel: 'Hapus semua'
        })

        if (!approved) return

        const result = actions.deleteAttendanceDate({ classId, date })

        if (result.ok) toast.success(result.message ?? 'Absensi dihapus.')

        set({ tanggal: DEMO_TODAY })
        reload()
    }

    return (
        <>
            <Seo title='Absen' />

            <PageHeader
                eyebrow='Kehadiran siswa'
                title='Catat absensi'
                description='Pilih kelas dan tanggal, lalu tentukan status kehadiran setiap siswa.'
                actions={(
                    <>
                        {classId && (
                            <ButtonLink href={`/jurnal?kelas=${classId}&tanggal=${date}`} variant='outline' size='sm'>
                                Lihat jurnal kelas ini
                                <ArrowRight aria-hidden='true' />
                            </ButtonLink>
                        )}

                        <ButtonLink href={`/preview?view=absen${classId ? `&kelas=${classId}` : ''}`} variant='outline' size='sm'>
                            Preview rekap siswa
                            <ArrowRight aria-hidden='true' />
                        </ButtonLink>

                        <ButtonLink href={`/laporan-harian?view=absen${classId ? `&kelas=${classId}` : ''}&tanggal=${date}`} variant='outline' size='sm'>
                            Lihat laporan harian
                            <ArrowRight aria-hidden='true' />
                        </ButtonLink>
                    </>
                )}
            />

            <Card className='mb-4 flex flex-col gap-3 p-4 md:flex-row md:items-center'>
                <div className='flex items-center gap-2.5'>
                    <span className='grid size-8 place-items-center rounded-lg bg-warning/12 text-warning' aria-hidden='true'>
                        <Zap className='size-4' />
                    </span>

                    <strong className='text-[13px] font-semibold text-foreground'>
                        Absensi massal:
                    </strong>
                </div>

                <div className='flex flex-1 flex-col gap-2 sm:flex-row'>
                    <Select
                        aria-label='Status absensi massal'
                        value={bulkStatus}
                        onChange={event => setBulkStatus(event.target.value as AttendanceStatus)}
                        className='h-9'
                        wrapperClassName='sm:w-36'
                    >
                        {ATTENDANCE_STATUSES.map(status => <option key={status} value={status}>{ATTENDANCE_LABELS[status]}</option>)}
                    </Select>

                    <Select
                        aria-label='Siswa yang diterapkan'
                        value={bulkMode}
                        onChange={event => setBulkMode(event.target.value as 'all' | 'empty')}
                        className='h-9'
                        wrapperClassName='sm:w-48'
                    >
                        <option value='all'>Semua siswa</option>
                        <option value='empty'>Hanya yang kosong</option>
                    </Select>

                    <Button size='sm' className='h-9' onClick={() => void handleBulk()} disabled={!classId || classStudents.length === 0}>
                        Terapkan
                    </Button>
                </div>
            </Card>

            <Card className='mb-6 grid gap-4 p-4 sm:p-5 lg:grid-cols-[minmax(0,14rem)_minmax(0,12rem)_minmax(0,1fr)] lg:items-end'>
                <Field label='Kelas' htmlFor='attendanceClass'>
                    <Select id='attendanceClass' value={classId} onChange={event => set({ kelas: event.target.value })}>
                        {classes.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </Select>
                </Field>

                <Field label='Tanggal absensi' htmlFor='attendanceDate'>
                    <Input
                        id='attendanceDate'
                        type='date'
                        value={date}
                        onChange={event => {
                            if (event.target.value) set({ tanggal: event.target.value })
                        }}
                    />
                </Field>

                <HistoryPicker
                    id='attendanceHistory'
                    title='Lihat absensi sebelumnya'
                    description='Pilih tanggal yang sudah memiliki catatan absensi.'
                    emptyText='Belum ada riwayat absensi untuk kelas ini.'
                    dates={historyDates}
                    value={date}
                    onSelect={value => set({ tanggal: value })}
                />
            </Card>

            {classStudents.length > 0 ? (
                <>
                    <AttendanceSheet
                        key={`${classId}-${date}-${version}`}
                        classId={classId}
                        date={date}
                        students={classStudents}
                        saved={saved}
                        onReload={reload}
                    />

                    {saved.size > 0 && (
                        <div className='mt-6 flex flex-col gap-3 rounded-xl border border-destructive/25 bg-destructive/5 p-4 sm:flex-row sm:items-center sm:justify-between'>
                            <div>
                                <p className='text-sm font-semibold text-foreground'>
                                    Hapus seluruh absensi tanggal ini?
                                </p>

                                <p className='text-[13px] text-muted-foreground'>
                                    Semua catatan kehadiran kelas terpilih pada {formatDate(date, 'short')} akan dihapus.
                                </p>
                            </div>

                            <Button variant='danger-outline' onClick={() => void handleDeleteDate()}>
                                <Trash2 aria-hidden='true' />
                                Hapus semua absensi tanggal ini
                            </Button>
                        </div>
                    )}
                </>
            ) : (
                <Card>
                    <EmptyState
                        icon={<UserPlus />}
                        title='Belum ada siswa di kelas ini'
                        description='Tambahkan data siswa terlebih dahulu agar absensi dapat dicatat.'
                        action={<ButtonLink href={classId ? `/siswa?kelas=${classId}` : '/siswa'}>Kelola data siswa</ButtonLink>}
                    />
                </Card>
            )}
        </>
    )
}

export default AttendancePage
